// POM-6249: the single way into Fill Later from the order toolbar.
//
// Replaces three buttons — two of which read "Move to Fill Later" identically — with one button
// and a menu that names each scope. The choices come from buildFillLaterOptions, so changing what
// the menu offers is an edit to that list, not to this file.
//
// The button is rendered only while at least one choice applies, and is absent otherwise. An order
// that can never go to Fill Later - already shipped, cancelled, transferred - has nothing to
// explain, and a permanently disabled control on every such order reads as something broken. The
// per-choice reasons still show inside the menu, where the distinction is between choices the user
// can act on now and ones they cannot.
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import useRoles from 'common/hooks/useRoles';
import MoveOrderToFillLaterDialog from './MoveOrderToFillLaterDialog';
import MoveToFillLaterDialog from './MoveToFillLaterDialog';
import SplitOrderByQuantityDialog from './SplitOrderByQuantityDialog';
import { FILL_LATER_OPTION, buildFillLaterOptions } from './fillLaterOptions';

const BUTTON_SX = {
  px: 1,
  fontSize: '0.75rem',
  minHeight: '30px',
  lineHeight: 'normal',
};

const FillLaterMenuButton = ({
  mpi,
  order,
  orderDetails,
  movableLineItems,
  canMoveToFillLater,
  splitSelectedLineItems,
  splitSkippedCount,
  moveableDialogLineItems,
  onActionComplete,
  disabled,
}) => {
  const hasAccess = useRoles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [openOption, setOpenOption] = useState(null);

  // The options were built from the order as it was when the menu opened; don't leave them up
  // once the caller has locked the button (e.g. the order is refreshing).
  useEffect(() => {
    if (disabled) setAnchorEl(null);
  }, [disabled]);

  const options = useMemo(
    () =>
      buildFillLaterOptions({
        order,
        hasAccess,
        movableLineItems,
        canMoveToFillLater,
        splitSelectedLineItems,
        splitSkippedCount,
      }),
    [
      order,
      hasAccess,
      movableLineItems,
      canMoveToFillLater,
      splitSelectedLineItems,
      splitSkippedCount,
    ]
  );

  const visibleOptions = options.filter((option) => !option.hidden);
  const orderOption = options.find((option) => option.id === FILL_LATER_OPTION.ORDER);
  const anyEnabled = visibleOptions.some((option) => option.enabled);

  const closeMenu = () => setAnchorEl(null);

  // POM-6320: closing the dialog does nothing but close it. Only a completed move tells the
  // caller, through each dialog's onSuccess — a cancel changed no order, so there is nothing to
  // refresh and no reason to put the screen back under a loader.
  const closeDialog = () => setOpenOption(null);

  const chooseOption = (id) => {
    setOpenOption(id);
    closeMenu();
  };

  // Hooks above run unconditionally; this is the first point it is safe to bail out.
  if (!anyEnabled) return null;

  return (
    <>
      <Button
        variant='outlined'
        size='small'
        endIcon={<KeyboardArrowDownIcon />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        disabled={disabled}
        data-testid='fill-later-menu-button'
        aria-haspopup='menu'
        aria-expanded={!!anchorEl}
        sx={BUTTON_SX}
      >
        Fill Later
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        // Eligibility reasons are written as full sentences for a tooltip, so without a cap
        // the widest one sets the menu width and drags it across the grid.
        slotProps={{ paper: { sx: { maxWidth: 280 } } }}
      >
        {visibleOptions.map((option) => (
          // A disabled MenuItem swallows pointer events, so the span is what the tooltip
          // listens on - without it the reason never appears on the one choice that needs it.
          <Tooltip
            key={option.id}
            title={option.enabled ? '' : option.reason || ''}
            placement='right'
            arrow
          >
            <span>
              <MenuItem
                disabled={!option.enabled}
                onClick={() => chooseOption(option.id)}
                data-testid={`fill-later-option-${option.id.toLowerCase()}`}
                sx={{ width: '100%' }}
              >
                <ListItemText
                  primary={option.label}
                  secondary={option.enabled ? option.hint : option.reason}
                  primaryTypographyProps={{ fontSize: '0.8125rem', fontWeight: 600 }}
                  secondaryTypographyProps={{
                    fontSize: '0.6875rem',
                    sx: { whiteSpace: 'normal' },
                  }}
                />
              </MenuItem>
            </span>
          </Tooltip>
        ))}
      </Menu>

      {/* Each dialog is mounted only while open so its form starts from the order and the
          selection as they are now, not as they were when the toolbar first rendered. */}
      {openOption === FILL_LATER_OPTION.ORDER && (
        <MoveOrderToFillLaterDialog
          orderAddress={order.shippingAddress}
          open
          onClose={closeDialog}
          onSuccess={onActionComplete}
          mpi={mpi}
          orderId={order.orderId}
          lineItems={orderOption?.lineItems}
        />
      )}

      {openOption === FILL_LATER_OPTION.ITEMS && (
        <MoveToFillLaterDialog
          orderAddress={order.shippingAddress}
          open
          onClose={closeDialog}
          onSuccess={onActionComplete}
          mpi={mpi}
          orderId={order.orderId}
          orderDetails={orderDetails}
          lineItems={moveableDialogLineItems}
        />
      )}

      {openOption === FILL_LATER_OPTION.QUANTITY && (
        <SplitOrderByQuantityDialog
          orderAddress={order.shippingAddress}
          open
          onClose={closeDialog}
          onSuccess={onActionComplete}
          mpi={mpi}
          orderId={order.orderId}
          lineItems={splitSelectedLineItems}
        />
      )}
    </>
  );
};

FillLaterMenuButton.propTypes = {
  mpi: PropTypes.string.isRequired,
  order: PropTypes.shape({
    orderId: PropTypes.string.isRequired,
    shippingAddress: PropTypes.shape({}),
    status: PropTypes.string,
    isProcessedExternal: PropTypes.bool,
    lineItems: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  orderDetails: PropTypes.shape({}).isRequired,
  movableLineItems: PropTypes.arrayOf(PropTypes.shape({})),
  canMoveToFillLater: PropTypes.bool,
  splitSelectedLineItems: PropTypes.arrayOf(PropTypes.shape({})),
  splitSkippedCount: PropTypes.number,
  moveableDialogLineItems: PropTypes.arrayOf(PropTypes.shape({})),
  onActionComplete: PropTypes.func,
  disabled: PropTypes.bool,
};

FillLaterMenuButton.defaultProps = {
  movableLineItems: [],
  canMoveToFillLater: false,
  splitSelectedLineItems: [],
  splitSkippedCount: 0,
  moveableDialogLineItems: [],
  onActionComplete: null,
  disabled: false,
};

export default FillLaterMenuButton;
